"use client";

import React from "react";
import { TYPESOFORDERS } from "../../../contants";
import { useSelector, useDispatch } from "react-redux";
import {
  setActiveOrder,
  deleteLocalOrder,
} from "../../../redux/action/orderActions";
import { deleteLocalOrderOffline } from "../../../redux/action/offlineDataActions";

const getColor = (type) => {
  if (type === 1) {
    return { backgroundColor: "#ffeaad" };
  }
  if (type === 2) {
    return { backgroundColor: "#c6ffba" };
  }
  if (type === 0) {
    return { backgroundColor: "#e3c8fa" };
  }
};

const OrderSummaryHeaderWithSelector = ({
  currentOrder,
  onTabChange,
  selectedTab = "active",
}) => {
  const dispatch = useDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  // Get active orders and network status from Redux
  const { activeOrders } = useSelector((state) => state.order);
  const { isOnline, isAppOffline } = useSelector(
    (state) => state.networkStatus
  );
  const isElectron = window?.api?.isElectron;

  const tabs = [
    // { value: "active", label: "Active", icon: "📋" },
    ...TYPESOFORDERS.map((type) => ({
      value: type.value,
      label: type.key,
    })),
  ];

  const handleTabSelect = (tabValue) => {
    onTabChange?.(tabValue);
  };

  // Filter orders based on selected tab
  const getFilteredOrders = () => {
    if (selectedTab === "active") {
      return currentOrder ? [currentOrder] : [];
    } else {
      return activeOrders.filter((order) => order.orderTypeId === selectedTab);
    }
  };

  const handleOrderClick = (order) => {
    dispatch(setActiveOrder(order.refId));
    setIsDropdownOpen(false);
  };

  const handleDeleteOrder = (e, order) => {
    e.stopPropagation(); // Prevent order activation when deleting
    e.preventDefault(); // Prevent any default behavior

    // Delete without confirmation
    if ((isAppOffline || !isOnline) && isElectron) {
      dispatch(deleteLocalOrderOffline(order.refId));
    } else {
      dispatch(deleteLocalOrder(order.refId));
    }
    
    setIsDropdownOpen(false);
  };

  const filteredOrders = getFilteredOrders();

  return (
    <div
      className="order-summary-header-wrapper cursor-pointer"
      onMouseEnter={() => setIsDropdownOpen(true)}
      onMouseLeave={() => setIsDropdownOpen(false)}
    >
      {/* Main Header */}
      <div className="header-content px-3">
        <div className="header-left">
          <h2 className="header-title m-0">Order Summary <span className="text-truncate">{currentOrder?.customerName ? `(${currentOrder.customerName})` : ""}</span></h2>
          {currentOrder && (
            <div className="header-info">
              <span className={`info-badge ${currentOrder.isOrderConfirmed ? "bg-success" : ""}`}>
                <i className="mdi mdi-table-furniture"></i>{" "}
                {currentOrder.tableNumber || currentOrder.orderType}
              </span>
              {currentOrder.branchOrderNumber && (
                <span className="info-number">
                  #{currentOrder.branchOrderNumber}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="header-right">
          {currentOrder?.associatedPerson && (
            <div className="person-info">
              <i className="mdi mdi-account"></i>{" "}
              <span className="person-name">
                {currentOrder?.associatedPerson}
              </span>
            </div>
          )}
          {/* Dropdown Trigger */}
          <div className="dropdown-trigger d-none" title="View all orders">
            <span className="trigger-label">
              {tabs.find((t) => t.value === selectedTab)?.label || "Active"}
            </span>
            <i
              className={`mdi mdi-chevron-down trigger-icon ${
                isDropdownOpen ? "open" : ""
              }`}
            ></i>
          </div>
        </div>
      </div>

      {/* Dropdown with Tabs and Orders List */}
      {isDropdownOpen && (
        <div className="dropdown-menu-custom z-1000">
          {/* Tabs */}
          <div className="dropdown-tabs-custom">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                className={`tab-custom ${
                  selectedTab === tab.value ? "active" : ""
                }`}
                onClick={() => handleTabSelect(tab.value)}
              >
                <span className="tab-custom-label">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Orders List */}
          <div className="orders-list-dropdown">
            {filteredOrders.length === 0 ? (
              <div className="text-center text-muted py-3">
                <i
                  className="mdi mdi-information-outline"
                  style={{ fontSize: "32px", opacity: 0.5 }}
                ></i>
                <p className="mb-0 small">
                  No {selectedTab === "active" ? "active" : ""} orders
                </p>
              </div>
            ) : (
              filteredOrders.map((order) => {
                const isActive = order.refId === currentOrder?.refId;
                const orderDeletable =
                  !order.orderItems || order.orderItems.length === 0;
                const isEditMode = order?.isBillPrinted;

                return (
                  <div
                    key={order.refId}
                    className={`order-card-dropdown ${
                      isActive ? "active" : ""
                    }`}
                    style={getColor(order.orderTypeId)}
                  >
                    <div className="order-card-header">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        {/* Make this div clickable to activate order */}
                        <div
                          className="flex-grow-1 cursor-pointer"
                          onClick={() => handleOrderClick(order)}
                          style={{ minWidth: 0, cursor: 'pointer' }}
                        >
                          <span className="fw-bold">
                            {order.tableNumber
                              ? `Table ${order.tableNumber}`
                              : `${order.orderType} ${order.customerName ? `- (${order.customerName})` : ""}`}
                          </span>
                          <span className="ml-2 badge badge-light badge-sm">
                            {order.orderItems?.length || 0} items
                          </span>
                        </div>

                        {/* Delete Button - Only show if order has no items */}
                        {orderDeletable && !isEditMode && (
                          <button
                            className="btn btn-sm p-0 ml-2 order-delete-btn"
                            onClick={(e) => handleDeleteOrder(e, order)}
                            onMouseDown={(e) => e.stopPropagation()} // Extra safety
                            title="Delete order"
                            style={{ zIndex: 10, position: "relative" }} // Ensure it's clickable
                          >
                            <i
                              className={`mdi mdi-close-circle ${
                                isActive ? "text-primary" : "text-danger"
                              }`}
                            ></i>
                          </button>
                        )}
                      </div>

                      {/* Bottom section also clickable */}
                      <div
                        className="d-flex justify-content-between align-items-center cursor-pointer"
                        onClick={() => handleOrderClick(order)}
                      >
                        {order.branchOrderNumber && (
                          <span
                            className={`badge ${
                              isActive ? "badge-dark" : "badge-primary"
                            } badge-sm`}
                          >
                            #{order.branchOrderNumber}
                          </span>
                        )}

                        {order.isOrderConfirmed && (
                          <div>
                            <span className="badge badge-warning badge-sm mr-1">
                              Confirmed
                            </span>
                            {order.isPaid && (
                              <span className="badge badge-success badge-sm">
                                Paid
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderSummaryHeaderWithSelector;
